import React, { useState, useEffect } from 'react';
import { clientService, Client } from '../services/clientService';
import { utilityService } from '../services/utilityService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    collectionCentre: '',
    date: new Date().toISOString()
  });

  const collectionCentres = ['Nukyat', 'Parotion', 'Tiriita'];

  const { currentUser, userProfile, userPermissions, selectedAgent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    loadClients();
  }, [currentUser, navigate]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError('');
      if (!userProfile) {
        setError('User profile not available');
        return;
      }
      const data = await clientService.getClients(userProfile.role, selectedAgent || undefined);
      setClients(data);
    } catch (err) {
      console.error('Error loading clients:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load clients. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      if (!userProfile) {
        setError('User profile not available');
        return;
      }
      
      if (editingClient) {
        // Update existing client
        await clientService.updateClient(editingClient.id!, formData, userProfile.role, selectedAgent || undefined);
        setClients(prevClients => 
          prevClients.map(client => 
            client.id === editingClient.id 
              ? { ...client, ...formData }
              : client
          )
        );
      } else {
        // Add new client
        const newClient = await clientService.addClient(formData, userProfile.role, selectedAgent || undefined);
        setClients(prevClients => [newClient, ...prevClients]);
      }
      setShowForm(false);
      setEditingClient(null);
      setFormData({ 
        name: '', 
        phoneNumber: '', 
        collectionCentre: '',
        date: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error saving client:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save client. Please try again.');
      }
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      phoneNumber: client.phoneNumber,
      collectionCentre: client.collectionCentre,
      date: client.date
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        setError('');
        if (!userProfile) {
          setError('User profile not available');
          return;
        }
        await clientService.deleteClient(id, userProfile.role, selectedAgent || undefined);
        setClients(prevClients => prevClients.filter(client => client.id !== id));
      } catch (err) {
        console.error('Error deleting client:', err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to delete client. Please try again.');
        }
      }
    }
  };

  const handleExport = () => {
    utilityService.exportClientsToCSV(clients);
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <div className="page-header">
          <h1>Clients</h1>
          <div className="header-actions">
            {userPermissions?.canAddClients && (
              <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
                {showForm ? 'Cancel' : 'Add New Client'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={handleExport}>
              Export to CSV
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <Dialog 
          open={showForm} 
          onClose={() => setShowForm(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingClient ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
          <DialogContent>
            <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Collection Centre</label>
                <select
                  className="form-input"
                  value={formData.collectionCentre}
                  onChange={(e) => setFormData({ ...formData, collectionCentre: e.target.value })}
                  required
                  disabled={userProfile?.role === 'admin'}
                >
                  <option value="">Select a collection centre</option>
                  {collectionCentres.map((centre) => (
                    <option key={centre} value={centre}>
                      {centre}
                    </option>
                  ))}
                </select>
                {userProfile?.role === 'admin' && (
                  <small className="form-text text-muted">
                    Collection centre will be automatically set based on your agent type
                  </small>
                )}
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {editingClient ? 'Update Client' : 'Add Client'}
            </Button>
          </DialogActions>
        </Dialog>

        {loading ? (
          <div className="loading">Loading clients...</div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Collection Centre</th>
                  <th>Date Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{client.phoneNumber}</td>
                    <td>{client.collectionCentre}</td>
                    <td>{utilityService.formatDate(client.date)}</td>
                    <td>
                      {userPermissions?.canModifyClients && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(client)}
                        >
                          Edit
                        </button>
                      )}
                      {userPermissions?.canModifyClients && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(client.id!)}
                          style={{ marginLeft: '0.5rem' }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Paper>
    </Container>
  );
};

export default Clients; 